<script setup lang="ts">
import { goToNotificationTarget } from '@/service/notificationsActions';
import type { NotificationRow } from '@/types/notifications';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import { useToast } from 'primevue/usetoast';
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n({
  useScope: 'global',
});
const router = useRouter();
const toast = useToast();

/**
 * @property {NotificationRow[]} items - Notifications to render (most recent first).
 * @property {number} unreadCount - Number of unread notifications.
 * @property {boolean} visible - Whether the dropdown is visible.
 */
const props = defineProps<{
  items: NotificationRow[];
  unreadCount: number;
  visible: boolean;
}>();

/**
 * @event close - Request to close the dropdown.
 * @event mark-read - When user marks a notification as read.
 * @event mark-unread - When user marks a notification as unread.
 * @event remove - When user deletes a notification.
 * @event open - When user opens a notification for details.
 * @event load-more - Request to load more notifications.
 */
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'mark-read', id: string): void;
  (e: 'mark-unread', id: string): void;
  (e: 'remove', id: string): void;
  (e: 'open', id: string): void;
  (e: 'load-more'): void;
}>();

const hasItems = computed(() => (props.items?.length ?? 0) > 0);

/**
 * Limit the dropdown to render at most 7 notifications.
 * The UI will show ~3 at once with scroll, per requirement.
 */
const limitedItems = computed(() => (props.items ?? []).slice(0, 7));

function resolveDataField(
  field: string,
  data: Record<string, unknown> | string | undefined | null,
): string {
  const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
  const name = String((parsedData as unknown as { [key: string]: string })?.[field] ?? '');
  return name || `(${t('common.name_not_available')})`;
}

function translatePreset(
  key: string | null | undefined,
  isTitle: boolean,
  fallback: string,
  data: Record<string, unknown> | string | undefined | null,
): string {
  if (!key || typeof key !== 'string') return fallback;
  const fullKey = `notifications.presets.${isTitle ? 'titles' : 'bodies'}.${key}`;
  const translated = t(fullKey, {
    device_name: resolveDataField(
      'device_name',
      data as Record<string, unknown> | string | undefined | null,
    ),
    anomaly_id: resolveDataField(
      'anomaly_id',
      data as Record<string, unknown> | string | undefined | null,
    ),
  });
  return translated === fullKey ? fallback : (translated as string);
}

// Full text dialog state
const selected = ref<NotificationRow | null>(null);
function openDialog(n: NotificationRow) {
  selected.value = n;
  emit('close');
}
function closeDialog() {
  selected.value = null;
}

// Loading state for navigation actions
const navigationLoading = reactive(new Map<string, boolean>());

/** Handle notification open action - show toast if already on page, close dropdown only on actual navigation */
async function handleOpen(n: NotificationRow) {
  navigationLoading.set(n.id, true);
  try {
    const result = await goToNotificationTarget(n);
    if (result.alreadyOnPage) {
      toast.add({
        group: 'notifications',
        severity: 'info',
        summary: t('notifications.common.already_on_page'),
        life: 3000,
      });
    } else if (result.navigated) {
      emit('close');
    }
  } finally {
    navigationLoading.set(n.id, false);
  }
}

// Root ref and outside-click handling (use bubbling to avoid bell-toggle race)
const dropdownRoot = ref<HTMLElement | null>(null);
function onDocClick(e: MouseEvent) {
  if (!props.visible) return;
  const root = dropdownRoot.value;
  if (!root) return;
  const target = e.target as Node | null;
  if (target && !root.contains(target)) {
    emit('close');
  }
}
onMounted(() => document.addEventListener('click', onDocClick, false));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick, false));
</script>

<template>
  <div
    v-if="visible"
    class="fixed md:absolute
    top-14 md:top-full
    left-1/2 md:left-auto md:right-0
    transform -translate-x-1/2 md:transform-none md:translate-x-0
    w-[calc(100vw-3rem)] md:w-[28rem] max-h-[70vh]
    overflow-y-auto overflow-x-hidden
    z-[1000]
    border border-surface-200 dark:border-surface-600
    shadow-sm shadow-white dark:shadow-black
    bg-surface-100 dark:bg-surface-800
    rounded-md"
    @click.stop
    ref="dropdownRoot"
  >
    <div class="flex items-center justify-between px-3 py-2">
      <span class="text-lg font-medium">{{ t('notifications.common.notifications') }}</span>
      <!-- Unread count & Close button -->
      <div class="flex items-center gap-2">
        <!-- Unread count -->
        <span class="text-sm text-gray-500 dark:text-gray-400">{{ t('notifications.common.unread') }}: {{ unreadCount }}</span>
        <!-- Close button -->
        <Button
          severity="secondary"
          class="p-button p-button-text p-button-rounded p-button-sm"
          :title="t('common.close') || 'Close'"
          @click="emit('close')"
        >
          <span class="pi pi-times text-lg"></span>
        </Button>
      </div>
    </div>
    <!-- Divider -->
    <div class="w-full h-[1px] mb-2 bg-surface-300 dark:bg-surface-600"></div>
    <!-- Notifications list -->
    <div>
      <!-- No notifications -->
      <div v-if="!hasItems" class="px-3 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
        {{ t('notifications.common.no_notifications') }}
      </div>
      <!-- List items -->
      <ul v-else class="list-none m-0 p-0 max-h-[22rem] overflow-y-auto">
        <li v-for="(n, index) in limitedItems" :key="n.id" class="relative flex px-3 py-2
        bg-surface-100 dark:bg-surface-600"
        :class="{ 'bg-surface-100 dark:bg-surface-700': n.is_read }"
        >
          <!-- Notification content -->
          <div class="flex items-start justify-between gap-2 pr-16 w-9/12">
            <div class="flex flex-col w-full cursor-pointer" @click="openDialog(n)">
              <!-- Title -->
              <span class="font-medium leading-tight" :class="{ 'text-primary': !n.is_read, 'text-gray-600 dark:text-gray-400 line-through': n.is_read }">{{ translatePreset(n.title, true, n.title, n.data) }}</span>
              <!-- Body clamped with fade -->
              <div class="relative max-h-20 overflow-hidden pr-2 mt-0.5 fade-out-mask">
                <span
                  v-if="n.body"
                  class="block text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line"
                  :class="{ 'line-through': n.is_read }"
                >{{ translatePreset(n.body, false, n.body, n.data) }}</span>
              </div>
              <!-- Created at (received at) -->
              <span class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ new Date(n.created_at).toLocaleString() }}</span>
            </div>
          </div>
          <!-- Action icon-buttons (bottom-right) -->
          <div class="flex items-center justify-end w-3/12">
            <!-- Open-->
            <Button
              :icon="navigationLoading.get(n.id) ? undefined : 'pi pi-external-link'"
              severity="secondary" variant="text" rounded
              :title="t('notifications.common.open')"
              :disabled="navigationLoading.get(n.id)"
              @click="handleOpen(n)"
            >
              <template v-if="navigationLoading.get(n.id)">
                <ProgressSpinner style="width: 16px; height: 16px" strokeWidth="4" />
              </template>
            </Button>
            <!-- Mark as read -->
            <Button
              v-if="!n.is_read"
              severity="secondary" icon="pi pi-check" variant="text" rounded
              :title="t('notifications.common.mark_as_read')"
              @click="emit('mark-read', n.id)"
            />
            <!-- Mark as unread -->
            <Button
              v-else
              severity="secondary" icon="pi pi-undo" variant="text" rounded
              :title="t('notifications.common.mark_as_unread')"
              @click="emit('mark-unread', n.id)"
            />
            <!-- Delete -->
            <Button
              severity="danger" icon="pi pi-trash" variant="text" rounded
              :title="t('notifications.common.delete')"
              @click="emit('remove', n.id)"
            />
          </div>
          <!-- Divider (except for last item) -->
          <div v-if="index < limitedItems.length - 1" class="absolute bottom-0 left-3 right-3 h-[1px] bg-surface-400 dark:bg-surface-500"></div>
        </li>
      </ul>
      <!-- Load more -->
      <div class="px-3 py-2 flex justify-end">
        <Button class="p-button p-button-text p-button-sm" @click="emit('close'); router.push('/notifications')">{{ t('notifications.common.open_notifications') }}</Button>
      </div>
    </div>
  </div>
  <div v-else />

  <!-- Full-text dialog -->
  <teleport to="body">
    <!-- Full-text dialog -->
    <div v-if="selected" class="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 dark:bg-black/40 p-4">
      <!-- Dialog -->
      <div class="w-full max-w-xl bg-surface-50 dark:bg-surface-800 rounded-md shadow-lg ring-1 ring-black/10 p-4">
        <!-- Title & Body -->
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1">
            <!-- Title -->
            <div class="font-semibold" :class="{ 'line-through': selected?.is_read }">{{ translatePreset(selected.title, true, selected.title ?? '', selected.data) }}</div>
            <!-- Body -->
            <div class="mt-2 max-h-[60vh] overflow-auto text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line" :class="{ 'line-through': selected?.is_read }">{{ translatePreset(selected.body, false, selected.body ?? '', selected.data) }}</div>
            <!-- Created at (received at) -->
            <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">{{ new Date(selected.created_at).toLocaleString() }}</div>
          </div>
          <!-- Close button at top-right -->
          <Button class="p-button p-button-text" @click="selected = null"><span class="pi pi-times"></span></Button>
        </div>
        <!-- Action buttons (bottom) -->
        <div class="mt-3 flex justify-end gap-2">
          <!-- Open -->
          <Button
            class="p-button p-button-text p-button-sm mx-1"
            :title="t('notifications.common.open')"
            :disabled="selected && navigationLoading.get(selected.id)"
            @click="selected && handleOpen(selected)"
          >
            <template v-if="selected && navigationLoading.get(selected.id)">
              <ProgressSpinner style="width: 16px; height: 16px" strokeWidth="4" class="mr-2" />
            </template>
            <template v-else>
              <span class="pi pi-external-link mr-2"></span>
            </template>
            {{ t('notifications.common.open') }}
          </Button>
          <Button
            v-if="selected && !selected.is_read"
            class="p-button p-button-text p-button-sm mx-1"
            :title="t('notifications.common.mark_as_read')"
            @click="() => { if (selected) { emit('mark-read', selected.id); selected.is_read = true; selected = null; } }"
          >
            <span class="pi pi-check mr-2"></span>{{ t('notifications.common.mark_as_read') }}
          </Button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped>
.fade-out-mask {
  /* Fade contents (text) to transparent towards the bottom */
  -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%);
  mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
}
</style>


